import React, { useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Customer,
  CustomerContext,
  SubscriptionContext,
  useSubscription
} from 'ui-smartforce-settings';
import { OnlineStatusContext } from '../../../../../Context/OnlineStatusContext';
import { SFScrollableRefHandler } from 'sfui';
import {
  ProjectsDashboardResponse,
  ProjectsTotals,
  ProjectValue
} from '../../../../../Models/ProjectValue';
import { TinyWidgetTotalsProps } from '../../../../../Components/TinyWidgets/TinyWidgetsContent/TinyWidgetsContent';
import { handleError } from '../../../../../Helpers';
import {
  getNextProjectsReports,
  getProjectsReports,
  getTotals
} from '../../../../../Services/ProjectsService';
import {
  durationTimeFormatStyled,
  getWidgetOrder,
  getWidgetTitle
} from '../helpers';

export interface UseProjectsDashboardProps {}

export interface UseProjectsDashboardReturn {
  isLoading: boolean;
  isFetchingMoreProjects: boolean;
  refScrollable: React.RefObject<SFScrollableRefHandler>;
  refNext: React.RefObject<string | undefined>;
  projects: ProjectValue[];
  totals: ProjectsTotals | undefined;
  tinyWidgetsTotals: TinyWidgetTotalsProps[];
  onScrollBottom: () => void;
}

export const useProjectsDashboard = (
  props: UseProjectsDashboardProps
): UseProjectsDashboardReturn => {
  const history = useHistory();
  const location = useLocation();
  const timezone = (useContext(CustomerContext).customer as Customer).timezone;
  const { isOnline } = useContext(OnlineStatusContext);
  const { subscriptionsSummary } = useContext(SubscriptionContext);
  const subscription = useSubscription('cc');

  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isFetchingMoreProjects, setIsFetchingMoreProjects] =
    React.useState<boolean>(true);

  const refNext = React.useRef<string | undefined>();
  const refScrollable = React.useRef<SFScrollableRefHandler>(null);

  const [projects, setProjects] = React.useState<ProjectValue[]>([]);
  const [totals, setTotals] = React.useState<ProjectsTotals | undefined>();

  React.useEffect(() => {
    let isSubscribed: boolean = true;

    const getProjects = async () => {
      try {
        setIsLoading(true);

        const response: ProjectsDashboardResponse = await getProjectsReports();
        const totalsResponse: ProjectsTotals = await getTotals();

        if (isSubscribed) {
          refNext.current = response.links.next;
          setProjects(response.projects);
          setTotals(totalsResponse);
          setIsLoading(false);
          setIsFetchingMoreProjects(false);
        }
      } catch (e) {
        console.error('ProjectsDashboard::getProjectsReports', e);
        handleError(e, history);
      }
    };

    if (isOnline) getProjects();

    return () => {
      isSubscribed = false;
    };
  }, [
    history,
    location.search,
    isOnline,
    timezone,
    subscriptionsSummary,
    subscription
  ]);

  const onScrollBottom = async () => {
    if (!!refNext && !!refNext.current && !isFetchingMoreProjects) {
      setIsFetchingMoreProjects(true);
      try {
        const response = await getNextProjectsReports(refNext.current);
        setProjects([...projects, ...response.projects]);
        setIsFetchingMoreProjects(false);
        refNext.current = response.links.next;
      } catch (e) {
        console.error('ProjectsDashboard::getMoreReports', e);
        handleError(e, history);
      }
    }
  };

  // Prepare totals array for TinyWidgets component
  const tinyWidgetsTotals: TinyWidgetTotalsProps[] = React.useMemo(() => {
    const totalsMap: TinyWidgetTotalsProps[] = [];
    Object.keys(totals || {}).forEach((key) => {
      totalsMap.push({
        title: getWidgetTitle(key),
        value: totals![key],
        order: getWidgetOrder(key),
        getCustomFormattedValue:
          key === 'time_spent' ? durationTimeFormatStyled : undefined
      });
    });
    return totalsMap.sort((a, b) => {
      if (a.order && b.order) return a.order - b.order;
      return -1;
    });
  }, [totals]);

  return {
    isLoading,
    isFetchingMoreProjects,
    refScrollable,
    refNext,
    projects,
    totals,
    tinyWidgetsTotals,
    onScrollBottom
  };
};
